/**
 * لوح المهام.
 *
 * أعمدة تُسحب بينها البطاقات. وهو **للمهام وحدها** لا لكل الأنشطة:
 * الاجتماع والمكالمة لهما موعد لا حالة عمل — «اجتماعٌ جارٍ» يعني أنه
 * منعقد الآن لا أن أحدًا يشتغل عليه، ووضعه في عمود «جارٍ» يجعل اللوح
 * يكذب على من يقرؤه.
 *
 * ## العلاقة بحالة النشاط
 *
 * الحالة `status` تبقى صاحبة القول في «أُنجز أم لا»، والمرحلة `boardStage`
 * تفصّل ما **دون** الإنجاز فقط. مصدران للحقيقة الواحدة يعني بطاقةً في
 * عمود «منجز» وحالتُها «مجدول» في التقارير — ولا يُعرف أيّهما يُصدَّق.
 *
 * فالخادم يُلازم بينهما في كل كتابة:
 *
 *   - النقل إلى «منجز»    ⇒ `status = DONE`     و`completedAt` الآن
 *   - النقل إلى ما دونه   ⇒ `status = PLANNED`  و`completedAt` فارغ
 *   - `CANCELLED`         ⇒ خارج اللوح أصلًا
 *
 * والإنجاز من شاشة الأنشطة يحرّك البطاقة، والسحب في اللوح يُنجز المهمة —
 * فعلٌ واحد مهما اختلف مدخله.
 */
import type { Labelled } from './common.js';

/* ==========================================================================
   الأعمدة
   ========================================================================== */

export const BOARD_STAGES = ['TODO', 'DOING', 'BLOCKED', 'DONE'] as const;
export type BoardStage = (typeof BOARD_STAGES)[number];

export interface BoardStageDef extends Labelled<BoardStage> {
  /** ما يعنيه العمود، يُعرض تحت عنوانه — عمودٌ بلا تعريف يُملأ بما لا يخصّه. */
  hint: string;
}

export const BOARD_STAGE_DEFS: readonly BoardStageDef[] = [
  { key: 'TODO', label: 'للتنفيذ', hint: 'مقبولة ولم تبدأ', kind: 'neutral' },
  { key: 'DOING', label: 'جارٍ العمل', hint: 'بدأت ولم تكتمل', kind: 'info' },
  /*
   * المتعثّرة عمودٌ لا وسم.
   *
   * وسمٌ على بطاقة في «جارٍ العمل» يختفي بين البطاقات، فيظنّ المدير أن
   * العمل يسير. وعمودٌ مستقلّ يجعل العدد ظاهرًا: ثلاثٌ متعثّرة سؤالٌ
   * يُطرح في الاجتماع.
   */
  { key: 'BLOCKED', label: 'متعثّرة', hint: 'تنتظر ردًّا أو قرارًا', kind: 'warning' },
  { key: 'DONE', label: 'منجزة', hint: 'اكتملت — آخر ثلاثين يومًا', kind: 'success' },
] as const;

export const BOARD_STAGE_MAP = Object.fromEntries(
  BOARD_STAGE_DEFS.map((d) => [d.key, d]),
) as Readonly<Record<BoardStage, BoardStageDef>>;

/**
 * أقصى ما يُعرض في العمود الواحد.
 *
 * عمودٌ فيه أربعمئة بطاقة لا يُقرأ ولا يُسحب فيه، ويُبطئ الصفحة كلها.
 * فيُعرض هذا القدر ويُذكر الباقي برقمه مع رابطٍ إلى القائمة المصفّاة —
 * إخفاءٌ معلن خيرٌ من عرضٍ لا يُستعمل.
 */
export const BOARD_COLUMN_LIMIT = 60;

/** كم يومًا يبقى المنجز على اللوح. */
export const BOARD_DONE_DAYS = 30;

/* ==========================================================================
   الشكل المعاد
   ========================================================================== */

export interface BoardCard {
  id: string;
  subject: string;
  body: string | null;
  priority: string;
  dueAt: string | null;
  completedAt: string | null;
  stage: BoardStage;
  assigneeId: string | null;
  /** السجل المرتبط — العميل أو العميل المحتمل، للسياق على البطاقة. */
  linkKind: 'lead' | 'account' | 'deal' | null;
  linkId: string | null;
  linkName: string | null;
  /** عدد الملاحظات عليها — «متعثّرة بلا ملاحظة» علامةُ سؤالٍ بلا جواب. */
  feedbackCount: number;
}

export interface BoardColumn {
  stage: BoardStage;
  /** العدد الكامل، لا عدد المعروض. */
  count: number;
  cards: BoardCard[];
}

export interface BoardView {
  columns: BoardColumn[];
  total: number;
  generatedAt: string;
}

/* ==========================================================================
   الترتيب داخل العمود
   ========================================================================== */

/**
 * ترتيبٌ محسوب لا مسحوب.
 *
 * أسانا تدع المستخدم يرتّب البطاقات بيده، وهو ما لا يُنقل كما هو: اللوح
 * هنا مشتركٌ بين فريق، وترتيبٌ يدويّ واحدٌ للجميع يعني أن ما يرفعه هذا
 * يُنزله ذاك، وترتيبٌ لكل مستخدم يعني أن «الأولى في اللوح» تختلف باختلاف
 * من ينظر — فيسقط معنى الجملة في الاجتماع.
 *
 * والمحسوب يجيب السؤال نفسه بلا خلاف: **الأقرب موعدًا أولًا** — فما فات
 * موعده يتقدّم من تلقاء نفسه لأن تاريخه أقدم — ثم الأهمّ عند تساوي
 * الموعد، وما لا موعد له في الآخر.
 *
 * والترتيب في قاعدة البيانات لا في المتصفح: العمود يُقتطع عند
 * `BOARD_COLUMN_LIMIT`، وترتيبٌ بعد الاقتطاع يرتّب ما وصل لا ما يجب أن
 * يصل — فتغيب المهمة المتأخّرة الحادية والستّون عن رأس العمود.
 */
export const BOARD_ORDER: readonly { field: string; dir: 'asc' | 'desc' }[] = [
  { field: 'dueAt', dir: 'asc' },
  { field: 'priority', dir: 'asc' },
] as const;

/** هل فات موعدها وما زالت غير منجزة. */
export function isOverdue(card: Pick<BoardCard, 'dueAt' | 'stage'>, now = new Date()): boolean {
  if (card.stage === 'DONE' || !card.dueAt) return false;
  return new Date(card.dueAt).getTime() < now.getTime();
}

/* ==========================================================================
   الملازمة بين العمود والحالة
   ========================================================================== */

/**
 * الحالة المقابلة للعمود — للسحب في اللوح.
 *
 * دالّتان لا شرطٌ مكرّر في ثلاثة مواضع: السحب، والإكمال من شاشة النشاط،
 * والتعديل المباشر. ثلاث نسخٍ من قاعدةٍ واحدة تنحرف عند أول تعديل، فتصير
 * البطاقة في «منجز» وحالتها «مجدول» — ولا يُعرف أيّهما يُصدَّق.
 */
export function statusForStage(stage: BoardStage): 'PLANNED' | 'DONE' {
  return stage === 'DONE' ? 'DONE' : 'PLANNED';
}

/**
 * العمود المقابل للحالة — للإكمال أو إعادة الفتح من خارج اللوح.
 *
 * وإعادة الفتح تُرجع البطاقة إلى «جارٍ العمل» لا «للتنفيذ»: من أعاد فتح
 * مهمة أنجزها إنما وجد فيها نقصًا، والعمل عليها قائم لا منتظَر.
 */
export function stageForStatus(
  status: 'PLANNED' | 'DONE' | 'CANCELLED',
  currentStage: BoardStage,
): BoardStage {
  if (status === 'DONE') return 'DONE';
  /* الملغاة تخرج من اللوح، فعمودها لا يُقرأ — ويبقى كما هو للرجوع إن فُتحت. */
  if (status === 'CANCELLED') return currentStage;
  return currentStage === 'DONE' ? 'DOING' : currentStage;
}
